import api from './api';
import { peekCache } from '../utils/cache/peek';
import type { TaskCreatePayload } from '../hrms/api/tasks';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface Conversation {
  id: string;
  type: 'dm' | 'group' | 'channel' | 'call';
  name: string | null;
  description: string | null;
  is_private: boolean;
  avatar_url: string | null;
  vanishing_ttl_s?: number | null;
  last_activity: string;
  unread_count?: number;
  members?: Member[];
  last_message?: Message | null;
  /** Cross-org group fields (Phases 1–8) */
  is_cross_org?: boolean;
  /**
   * False when the composer must stay closed. Today the only reason is
   * `awaiting_reply`: in an external DM you get one message to someone at
   * another company and nothing more until they answer. Undefined on
   * conversations the server has not judged (anything internal).
   */
  can_send?: boolean;
  frozen_reason?: 'awaiting_reply' | string | null;
  org_a_head_office_id?: string | null;
  org_a_branch_id?: string | null;
  org_b_head_office_id?: string | null;
  org_b_branch_id?: string | null;
  /** Phase 12: cross-org admin DM back-reference */
  parent_group_id?: string | null;
  /** WhatsApp-style "Message yourself": a DM whose only member is me */
  is_self?: boolean;
}

export interface Member {
  id?: string;
  user_id: string;
  name: string;
  email?: string;
  role: string;
  is_online?: boolean;
  last_seen_at?: string;
  designation?: string;
  department_name?: string;
  department_id?: string;
  phone?: string;
  /** Cross-org affiliation field (Phase 2) */
  member_head_office_id?: string | null;
  display_name?: string;
  avatar_url?: string | null;
}

export interface Reaction {
  emoji: string;
  count: number;
  user_ids: string[];
}

export interface Attachment {
  id: string;
  file_url: string;
  file_name: string;
  file_size: number;
  mime_type: string;
}

export interface Message {
  id: string;
  conversation_id: string;
  sender_id: string;
  sender_name?: string;
  sender_avatar?: string;
  content: string | null;
  message_type: 'text' | 'file' | 'voice' | 'image' | 'task_link' | 'system' | 'call_event' | 'poll';
  thread_parent_id: string | null;
  reply_to_id: string | null;
  is_edited: boolean;
  is_deleted: boolean;
  /** Set on the client when a message_deleted WS frame arrives. Not persisted on server. */
  deleted_by_id?: string | null;
  deleted_by_name?: string | null;
  deleted_by_self?: boolean;
  created_at: string;
  reactions?: Reaction[];
  attachments?: Attachment[];
  mentioned_users?: string[];
  forward_from_id?: string | null;
  transcription?: string | null;
  transcription_lang?: string | null;
  body?: Record<string, unknown> | null;
  /** Cross-org sender affiliation fields (Phase 5) */
  sender_company_name?: string | null;
  sender_org_brand_color?: string | null;
  anonymized_sender?: boolean;
  /**
   * WhatsApp-style delivery state for the *sender's own* messages:
   *   'sent'      → left this device / reached the server (single tick)
   *   'delivered' → received by all recipients (gray double tick)
   *   'seen'      → read by all recipients (blue double tick)
   * Populated by the server on history load; undefined for others' messages.
   */
  status?: 'sent' | 'delivered' | 'seen';
  /** Latest delivery timestamp across recipients (own messages). */
  delivered_at?: string | null;
  /** Latest read timestamp across recipients (own messages). */
  seen_at?: string | null;
  /**
   * This message's text was composed by Lio and sent by the user from the Lio
   * page, rather than typed here. SENDER-ONLY: the API returns it as `true`
   * exclusively to the person who sent the message, and strips it per
   * recipient at websocket fan-out. Recipients always see `false`, by design —
   * so never render this on a message that isn't the current user's.
   */
  is_ai_generated?: boolean;
}

/** A single recipient row in the "Message Info" view. */
export interface MessageInfoRecipient {
  user_id: string;
  name: string;
  avatar?: string | null;
  delivered_at?: string | null;
  seen_at?: string | null;
}

export interface MessageInfo {
  message_id: string;
  sender_id: string;
  sent_at: string | null;
  recipients: MessageInfoRecipient[];
}

export interface ChatNotification {
  conversation_id: string;
  conversation_type: 'dm' | 'group' | 'channel';
  conversation_name: string;
  message_id: string;
  sender_id: string;
  sender_name: string;
  sender_designation?: string;
  content: string;
  message_type: Message['message_type'];
  created_at: string;
}

// ─── Conversations ────────────────────────────────────────────────────────────

export const listConversations = (filters?: { department_id?: string; designation?: string; q?: string }) =>
  api.get<Conversation[]>('/conversations', { params: filters }).then(r => r.data);

/** Cached counterpart of listConversations. Must mirror its url and params. */
export const peekConversations = (filters?: { department_id?: string; designation?: string; q?: string }) =>
  peekCache<Conversation[]>('chat', '/conversations', filters as Record<string, unknown> | undefined);

export const deleteDm = (conversationId: string): Promise<void> =>
  api.delete(`/conversations/${conversationId}/dm`).then(() => undefined);

export const getConversation = (id: string) =>
  api.get<Conversation>(`/conversations/${id}`).then(r => r.data);

export const createConversation = (body: {
  type: 'group' | 'channel' | 'call';
  name: string;
  description?: string;
  is_private?: boolean;
  member_ids: string[];
}) => api.post<Conversation>('/conversations', body).then(r => r.data);

export const getOrCreateDm = (targetUserId: string) =>
  api.post<Conversation>(`/conversations/dm?target_user_id=${targetUserId}`).then(r => r.data);

export const updateConversation = (id: string, body: { name?: string; description?: string; avatar_url?: string | null; vanishing_ttl_s?: number | null }) =>
  api.patch<Conversation>(`/conversations/${id}`, body).then(r => r.data);

export const deleteConversation = (id: string): Promise<void> =>
  api.delete(`/conversations/${id}`).then(() => undefined);

// ─── Avatars (profile pictures) ───────────────────────────────────────────────

/** Upload/replace the current user's profile picture. Returns the new URL. */
export const uploadMyAvatar = (blob: Blob) => {
  const fd = new FormData();
  fd.append('file', blob, 'avatar.jpg');
  return api.post<{ avatar_url: string }>('/identity/me/avatar', fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }).then(r => r.data);
};

/** Remove the current user's Messenger photo; returns null when cleared. */
export const deleteMyAvatar = () =>
  api.delete<{ avatar_url: string | null }>('/identity/me/avatar').then(r => r.data);

/** Upload/replace a group's photo (admins only). */
export const uploadGroupAvatar = (conversationId: string, blob: Blob) => {
  const fd = new FormData();
  fd.append('file', blob, 'group.jpg');
  return api.post<{ id: string; avatar_url: string }>(`/conversations/${conversationId}/avatar`, fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }).then(r => r.data);
};

/** Remove a group's photo (admins only). */
export const deleteGroupAvatar = (conversationId: string) =>
  api.delete<{ id: string; avatar_url: string | null }>(`/conversations/${conversationId}/avatar`).then(r => r.data);

// ─── Members ─────────────────────────────────────────────────────────────────

export const addMembers = (conversationId: string, userIds: string[]) =>
  api.post(`/conversations/${conversationId}/members`, { user_ids: userIds }).then(r => r.data);

export const removeMember = (conversationId: string, userId: string) =>
  api.delete(`/conversations/${conversationId}/members/${userId}`).then(r => r.data);

export const getOnlineMembers = (conversationId: string) =>
  api.get<{ online: string[] }>(`/conversations/${conversationId}/members/online`).then(r => r.data);

// ─── Messages ─────────────────────────────────────────────────────────────────

export const getMessages = (conversationId: string, beforeId?: string, limit = 50) =>
  api.get<Message[]>(`/conversations/${conversationId}/messages`, {
    params: { before_id: beforeId, limit },
  }).then(r => r.data);

/**
 * A window of history centred on `messageId` — used to jump to a message that
 * is older than the currently loaded page (pinned bar, quoted reply, search
 * hit, notification deep link). Returns the target plus messages on both
 * sides of it.
 */
export const getMessagesAround = (conversationId: string, messageId: string, limit = 50) =>
  api.get<Message[]>(`/conversations/${conversationId}/messages`, {
    params: { around_id: messageId, limit },
  }).then(r => r.data);

/** Cached counterpart of getMessages. Must mirror its url and params. */
export const peekMessages = (conversationId: string, beforeId?: string, limit = 50) =>
  peekCache<Message[]>('chat', `/conversations/${conversationId}/messages`, {
    before_id: beforeId,
    limit,
  });

export type CreateMessageBody = {
  conversation_id?: string;
  content: string;
  message_type?: Message['message_type'];
  thread_parent_id?: string | null;
  reply_to_id?: string | null;
  mentioned_users?: string[];
  task_id?: string | null;
  body?: Record<string, unknown> | null;
  temp_id?: string;
  /** Mark the message as Lio-composed. Only the sender ever sees the badge. */
  is_ai_generated?: boolean;
};

export const createMessage = (conversationId: string, body: CreateMessageBody) =>
  // The API's MessageCreate schema requires conversation_id IN THE BODY, not
  // just the URL path — omitting it 422s every send. Injected here so no caller
  // has to remember; an explicit one in `body` still wins (the router rejects a
  // mismatch with the path, which is the check we want kept).
  api
    .post<Message>(`/conversations/${conversationId}/messages`, {
      conversation_id: conversationId,
      ...body,
    })
    .then(r => r.data);

export const editMessage = (messageId: string, content: string) =>
  api.patch<Message>(`/messages/${messageId}`, { content }).then(r => r.data);

export const deleteMessage = (messageId: string) =>
  api.delete(`/messages/${messageId}`).then(r => r.data);

/** Short-lived signed URL that forces `Content-Disposition: attachment` for a
 *  chat attachment, looked up by its stored file_url (404 if it isn't one). */
export const getAttachmentDownloadUrl = (fileUrl: string) =>
  api.get<{ url: string }>('/attachments/download-url', { params: { file_url: fileUrl } })
    .then(r => r.data.url);

export const markSeen = (conversationId: string, messageIds: string[]) =>
  api.post(`/conversations/${conversationId}/seen`, { message_ids: messageIds }).then(r => r.data);

export type MarkConversationUnreadBody = {
  message_id?: string;
};

export const markConversationUnread = (conversationId: string, body?: MarkConversationUnreadBody) =>
  api.post(`/conversations/${conversationId}/unread`, body ?? {}).then(r => r.data);

/** Fetch the per-recipient delivery/read breakdown for one message. */
export const getMessageInfo = (messageId: string) =>
  api.get<MessageInfo>(`/messages/${messageId}/info`).then(r => r.data);

// ─── Reactions ────────────────────────────────────────────────────────────────

export const toggleReaction = (messageId: string, emoji: string) =>
  api.post(`/messages/${messageId}/reactions`, { emoji }).then(r => r.data);

// ─── Files ────────────────────────────────────────────────────────────────────

export const uploadFile = (
  conversationId: string,
  file: File,
  threadParentId?: string,
  onProgress?: (loaded: number, total: number) => void,
  caption?: string,
) => {
  const form = new FormData();
  form.append('file', file);
  if (threadParentId) form.append('thread_parent_id', threadParentId);
  if (caption && caption.trim()) form.append('caption', caption.trim());
  return api.post<Message>(`/conversations/${conversationId}/files`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
    transformRequest: [(data) => data],
    onUploadProgress: (e) => {
      if (!onProgress) return;
      const total = e.total ?? file.size ?? 0;
      onProgress(e.loaded, total || e.loaded);
    },
  }).then(r => r.data);
};

// ─── Thread ───────────────────────────────────────────────────────────────────

export const getThread = (conversationId: string, parentId: string) =>
  api.get<Message[]>(`/conversations/${conversationId}/threads/${parentId}`).then(r => r.data);

// ─── Task integration ─────────────────────────────────────────────────────────

export const convertToTask = (
  messageId: string,
  body: TaskCreatePayload
) =>
  api.post<{ task_id: string; task_title: string }>(`/messages/${messageId}/convert-to-task`, body).then(r => r.data);

// ─── DBMS (folder-based document library) ───────────────────────────────────
export interface DbmsPerson {
  user_id: string;
  name?: string;
  email?: string;
  avatar_url?: string;
  is_self?: boolean;
}
export type DbmsOwnerType = 'user' | 'client' | 'vendor';
export interface DbmsScope {
  ownerType?: DbmsOwnerType;   // default 'user'
  ownerId?: string;            // client/vendor id (or a specific user)
  ownerName?: string;          // display-name snapshot (client/vendor)
}
export interface DbmsFolder {
  id: string;
  name: string;
  parent_id?: string | null;
  owner_type?: DbmsOwnerType;
  owner_user_id: string;
  file_count?: number;
  subfolder_count?: number;
  // Bytes of the files directly in this folder, and of the whole subtree.
  total_size?: number;
  total_size_recursive?: number;
  created_at?: string;
  // Who created/sent this folder — folders in different scopes can share a
  // name, so the author is what tells them apart.
  created_by?: string | null;
  created_by_name?: string | null;
  // Mirrored into MY Google Drive (Drive is per-viewer). True only when the
  // folder and everything under it is there, so a partial export still offers
  // the send action.
  in_drive?: boolean;
  drive_link?: string | null;
}
// Serialize a scope into query params / body fields for the API.
const scopeParams = (s?: DbmsScope) => ({
  ...(s?.ownerType && s.ownerType !== 'user' ? { owner_type: s.ownerType } : {}),
  ...(s?.ownerId ? { owner_id: s.ownerId } : {}),
});
export interface DbmsFile {
  id: string;
  file_name: string;
  mime_type?: string | null;
  file_size: number;
  created_at: string;
  is_image?: boolean;
  folder_id?: string | null;
  download_url?: string | null;
  conversation_id?: string | null;
  conversation_name?: string | null;
  message_id?: string | null;
  // Already copied into MY Google Drive (per-viewer, like folders above).
  in_drive?: boolean;
  drive_link?: string | null;
  /** Where the bytes live. 'drive' means the file went straight into the
   *  owner's Google Drive and we hold only a pointer: it uses none of their
   *  Teamlio quota, and `download_url` is a Drive PAGE, not the file itself —
   *  so it must never be used as an <img> source. Absent on an older
   *  backend, where every file is a bucket copy. */
  storage_backend?: 'gcs' | 'drive';
}

// ── Duplicate names ───────────────────────────────────────────────────────
// One owner's folder may hold only ONE thing under a given name — folders and
// files share the namespace, and the comparison ignores case and surrounding
// spaces. Every DBMS write that would break that comes back as a 409 carrying
// this shape, which the UI turns into the "already exists, rename it" popup.
export interface DbmsDuplicate {
  /** What is already sitting on the name. */
  kind: 'folder' | 'file';
  /** The name that was refused. */
  name: string;
  message: string;
}

/** The duplicate-name 409 behind an axios error, or null for anything else. */
export const dbmsDuplicateInfo = (e: any): DbmsDuplicate | null => {
  const d = e?.response?.data?.detail;
  if (e?.response?.status !== 409 || d?.error !== 'duplicate_name') return null;
  return {
    kind: d.kind === 'folder' ? 'folder' : 'file',
    name: String(d.name ?? ''),
    message: String(d.message ?? 'That name is already used in this folder.'),
  };
};

/** The same popup text, for a collision caught in the browser before any request. */
export const dbmsDuplicateMessage = (kind: 'folder' | 'file', name: string): string =>
  `A ${kind} named “${name}” already exists in this folder. ` +
  'Change the name and try again.';

// People the caller can file to (own head office, includes "me" first).
export const dbmsPeople = (q?: string) =>
  api.get<{ people: DbmsPerson[] }>(`/documents/people`, { params: q ? { q } : {} }).then(r => r.data.people);

// Split a tree by who put each item there: 'mine' = I created it (My DBMS),
// 'shared' = someone else sent it to me (Shared with me).
export type DbmsSource = 'mine' | 'shared';

// An owner's folder tree (flat list; build the tree from parent_id).
export const dbmsFolders = (scope?: DbmsScope, source?: DbmsSource) =>
  api
    .get<{ user_id: string; folders: DbmsFolder[] }>(`/documents/folders`, {
      params: { ...scopeParams(scope), ...(source ? { source } : {}) },
    })
    .then(r => r.data.folders);

// A SPECIFIC user's folder tree — explicit `user_id` param so it can never fall
// back to the caller's own DBMS (used by the Send-to-DBMS folder picker).
export const dbmsFoldersForUser = (userId: string) =>
  api
    .get<{ user_id: string; folders: DbmsFolder[] }>(`/documents/folders`, { params: { user_id: userId } })
    .then(r => r.data.folders);

export const dbmsCreateFolder = (name: string, parentId?: string | null, scope?: DbmsScope) =>
  api
    .post<DbmsFolder>(`/documents/folders`, {
      name, parent_id: parentId ?? null,
      owner_type: scope?.ownerType || 'user', owner_id: scope?.ownerId ?? null,
      owner_name: scope?.ownerName ?? null,
    })
    .then(r => r.data);

export const dbmsRenameFolder = (folderId: string, patch: { name?: string; parent_id?: string | null }) =>
  api.patch<DbmsFolder>(`/documents/folders/${folderId}`, patch).then(r => r.data);

export const dbmsDeleteFolder = (folderId: string) =>
  api.delete(`/documents/folders/${folderId}`).then(r => r.data);

// Files in a folder (omit folderId for root).
export const dbmsListDocuments = (
  opts: { scope?: DbmsScope; folderId?: string | null; q?: string; source?: DbmsSource } = {}
) =>
  api
    .get<{ files: DbmsFile[]; total: number }>(`/documents/documents`, {
      params: {
        ...scopeParams(opts.scope),
        ...(opts.folderId ? { folder_id: opts.folderId } : {}),
        ...(opts.q ? { q: opts.q } : {}),
        ...(opts.source ? { source: opts.source } : {}),
      },
    })
    .then(r => r.data);

export const dbmsMoveDocument = (docId: string, folderId?: string | null) =>
  api.post<DbmsFile>(`/documents/documents/${docId}/move`, { folder_id: folderId ?? null }).then(r => r.data);

export const dbmsRenameDocument = (docId: string, name: string) =>
  api.patch<DbmsFile>(`/documents/documents/${docId}`, { name }).then(r => r.data);

export const dbmsDeleteDocument = (docId: string) =>
  api.delete(`/documents/documents/${docId}`).then(r => r.data);

// Upload a file from the device into a DBMS folder.
export const dbmsUploadFile = (
  file: File,
  opts: {
    scope?: DbmsScope;
    folderId?: string | null;
    /** Bytes sent so far — drives the per-file progress bar. */
    onProgress?: (loaded: number, total: number) => void;
    /** Abort the request (the uploader's Cancel button). */
    signal?: AbortSignal;
  } = {}
) => {
  const fd = new FormData();
  fd.append('file', file);
  if (opts.scope?.ownerType) fd.append('owner_type', opts.scope.ownerType);
  if (opts.scope?.ownerId) fd.append('owner_id', opts.scope.ownerId);
  if (opts.scope?.ownerName) fd.append('owner_name', opts.scope.ownerName);
  if (opts.folderId) fd.append('folder_id', opts.folderId);
  return api
    // Backend route is /chat-api/documents/documents/upload (the doubled
    // "documents" matches list/move/rename/delete). A single-segment path 404s.
    .post<DbmsFile>(`/documents/documents/upload`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
      // The instance default is a 30s timeout, which a large file on a slow
      // connection blows through mid-transfer. Uploads are bounded by the
      // per-file size cap and the user's Cancel button instead.
      timeout: 0,
      signal: opts.signal,
      onUploadProgress: e => {
        if (!opts.onProgress) return;
        const total = e.total ?? file.size ?? 0;
        opts.onProgress(e.loaded, total || e.loaded);
      },
    })
    .then(r => r.data);
};

export interface DbmsDriveImportResult {
  ok: boolean;
  name?: string | null;
  is_folder: boolean;
  imported: number;
  /** Files already imported into this same folder from the same Drive item. */
  skipped: number;
  failed: number;
  /** Items refused because the destination already holds that name. */
  duplicates: number;
  bytes_imported: number;
  /** Of `imported`, how many were LINKED to the file in the user's own Drive
   *  rather than copied into Teamlio storage. Those move no bytes, so a
   *  `bytes_imported` of 0 is success, not failure — report them by count. */
  linked?: number;
  folder_id?: string | null;
  errors: string[];
}

// Pull one picked Drive item (file or folder) into a DBMS folder. Only the id
// travels: the server re-reads name/type/size from Drive, since the browser's
// copy is user-supplied and the size decides how much quota gets spent.
export const dbmsImportFromDrive = (
  driveFileId: string,
  opts: { scope?: DbmsScope; folderId?: string | null } = {}
) =>
  api
    .post<DbmsDriveImportResult>('/documents/drive/import', {
      drive_file_id: driveFileId,
      owner_type: opts.scope?.ownerType,
      owner_id: opts.scope?.ownerId,
      owner_name: opts.scope?.ownerName,
      folder_id: opts.folderId ?? null,
    })
    .then(r => r.data);

// Transfer target (whose DBMS + which folder to copy INTO).
export interface DbmsTransferTarget {
  target_owner_type?: DbmsOwnerType;
  target_owner_id: string;
  target_owner_name?: string;
  target_folder_id?: string | null;
}
export const dbmsTransferDocument = (docId: string, target: DbmsTransferTarget) =>
  api.post<DbmsFile>(`/documents/documents/${docId}/transfer`, {
    target_owner_type: target.target_owner_type || 'user',
    target_owner_id: target.target_owner_id,
    target_owner_name: target.target_owner_name ?? null,
    target_folder_id: target.target_folder_id ?? null,
  }).then(r => r.data);

export const dbmsTransferFolder = (folderId: string, target: DbmsTransferTarget) =>
  api.post<{ folders_copied: number; files_copied: number }>(`/documents/folders/${folderId}/transfer`, {
    target_owner_type: target.target_owner_type || 'user',
    target_owner_id: target.target_owner_id,
    target_owner_name: target.target_owner_name ?? null,
    target_folder_id: target.target_folder_id ?? null,
  }).then(r => r.data);

// File a message's attachment(s) into a chosen person's DBMS folder.
export const sendToDbms = (
  messageId: string,
  target: { target_user_id: string; folder_id?: string | null }
) =>
  api
    .post<{
      saved: number;
      already: number;
      failed: number;
      /** A DIFFERENT file whose name the destination folder already uses. */
      duplicates?: number;
      duplicate_names?: string[];
      owner_name?: string;
      folder_id?: string | null;
    }>(
      `/documents/chat/messages/${messageId}/send`,
      { target_user_id: target.target_user_id, folder_id: target.folder_id ?? null }
    )
    .then(r => r.data);

// ── DBMS storage quotas ───────────────────────────────────────────────────
export interface DbmsStorageUsage {
  owner_type: DbmsOwnerType;
  owner_id: string;
  owner_name?: string | null;
  used_bytes: number;
  // null when the owner is unlimited
  quota_bytes?: number | null;
  available_bytes?: number | null;
  file_count: number;
  unlimited: boolean;
  percent_used: number;
  is_custom_quota: boolean;
  recomputed_at?: string | null;
  /** Global per-file upload cap from server config — not a per-account value.
   *  Absent on an older backend, in which case skip the client-side check and
   *  let the server reject the file. */
  max_upload_bytes?: number | null;
  /** Files the owner keeps in their Google Drive instead of our bucket.
   *  Deliberately NOT part of `used_bytes` — they cost no Teamlio storage.
   *  Reported so the meter can say where files went, and so disconnecting
   *  Drive can warn about how much would stop being reachable from here. */
  drive_backed_files?: number;
  drive_backed_bytes?: number;
  /** The whole organisation's position against the ceiling Teamlio granted it.
   *  All absent/null when the organisation is ungoverned or unlimited — the
   *  meter then shows only the owner's own bar. Reported because a full
   *  organisation stops an owner who still has room of their own, and the meter
   *  has to be able to say which of the two limits bit. Only an admin can see
   *  the number; only Teamlio can change it. */
  workspace_quota_bytes?: number | null;
  workspace_used_bytes?: number | null;
  workspace_available_bytes?: number | null;
}

// The caller's own usage by default; pass a scope for a client/vendor DBMS.
export const dbmsStorageUsage = (scope?: DbmsScope) =>
  api.get<DbmsStorageUsage>(`/documents/storage/usage`, { params: scopeParams(scope) })
    .then(r => r.data);

// Admin: everyone's usage (tenant-scoped server-side).
export const dbmsStorageAccounts = (params?: { q?: string; owner_type?: DbmsOwnerType }) =>
  api.get<{ total: number; accounts: DbmsStorageUsage[] }>(`/documents/storage/accounts`, {
    params: { ...(params?.q ? { q: params.q } : {}), ...(params?.owner_type ? { owner_type: params.owner_type } : {}) },
  }).then(r => r.data);

// Admin: set one owner's cap. null = back to the server default, negative =
// unlimited, 0 = block all uploads.
export const dbmsSetQuota = (ownerType: DbmsOwnerType, ownerId: string, quotaBytes: number | null) =>
  api.patch<DbmsStorageUsage>(`/documents/storage/accounts/${ownerType}/${ownerId}`, {
    quota_bytes: quotaBytes,
  }).then(r => r.data);

// Admin: re-derive used_bytes from the source tables (fixes counter drift).
export const dbmsRecomputeStorage = (ownerType: DbmsOwnerType, ownerId: string) =>
  api.post<DbmsStorageUsage>(`/documents/storage/accounts/${ownerType}/${ownerId}/recompute`)
    .then(r => r.data);

// ── Google Drive export ───────────────────────────────────────────────────
export interface DriveExportResult {
  ok: boolean;
  drive_file_id?: string | null;
  web_view_link?: string | null;
  file_name?: string | null;
  already?: boolean;   // was already in Drive from an earlier export
}
export interface DriveFolderExportResult {
  ok: boolean;
  folder_name?: string | null;
  web_view_link?: string | null;
  exported: number;
  already: number;
  failed: number;
  bytes_exported: number;
}

// All three send to the CALLER's Drive, under "Teamlio DBMS/".
export const dbmsExportDocumentToDrive = (docId: string) =>
  api.post<DriveExportResult>(`/documents/documents/${docId}/drive-export`).then(r => r.data);

export const dbmsExportFolderToDrive = (folderId: string) =>
  api.post<DriveFolderExportResult>(`/documents/folders/${folderId}/drive-export`).then(r => r.data);

export const exportChatMessageToDrive = (messageId: string) =>
  api.post<DriveFolderExportResult>(`/documents/chat/messages/${messageId}/drive-export`)
    .then(r => r.data);

export const createTaskForConv = (
  conversationId: string,
  body: TaskCreatePayload
) =>
  api.post<{ task_id: string; task_title: string }>(`/conversations/${conversationId}/tasks`, body).then(r => r.data);

// ─── Pinned messages ─────────────────────────────────────────────────────────

export type Pin = {
  id: string;
  conversation_id: string;
  message_id: string;
  pinned_by: string | null;
  pinned_by_name?: string | null;
  pinned_at: string;
  message?: Message | null;
};

export const pinMessage = (messageId: string): Promise<Pin> =>
  api.post<Pin>(`/messages/${messageId}/pin`).then(r => r.data);

export const unpinMessage = (messageId: string): Promise<void> =>
  api.delete(`/messages/${messageId}/pin`).then(() => undefined);

export const listPinned = (conversationId: string): Promise<Pin[]> =>
  api.get<Pin[]>(`/conversations/${conversationId}/pinned`).then(r => r.data);

// ─── Search ───────────────────────────────────────────────────────────────────

export const searchMessages = (q: string, conversationId?: string, limit = 30) =>
  api.get<{ messages: Message[]; total: number } | Message[]>('/search', {
    params: { q, conversation_id: conversationId, limit },
  }).then(r => r.data);

export const listNotifications = (limit = 20) =>
  api.get<ChatNotification[]>('/notifications', { params: { limit } }).then(r => r.data);

// ─── Eligible users ───────────────────────────────────────────────────────────

export interface EligibleUser {
  user_id: string;
  id: string;
  name: string;
  email?: string;
  /**
   * The staff code HRMS shows on a person's own profile ("EMP010"). Present
   * on internal rows only — an external row carries name + company and
   * nothing else, though the code is still matched server-side.
   */
  employee_code?: string | null;
  avatar_url?: string;
  designation?: string;
  department_id?: string;
  department_name?: string;
  branch_id?: string;
  is_online?: boolean;
  // External chat: set on rows from /eligible-users/external. Those rows carry
  // a company label instead of designation/department/email, which the backend
  // strips for anyone outside the viewer's organization.
  is_external?: boolean;
  head_office_id?: string | null;
  company_name?: string | null;
  brand_color?: string | null;
}

export const getEligibleUsers = () =>
  api.get<EligibleUser[]>('/eligible-users').then(r => r.data);

export interface ExternalUserPage {
  items: EligibleUser[];
  /** Pass back as `cursor` for the next page; null when the list is exhausted. */
  next_cursor: string | null;
}

/**
 * People at every other organisation on the platform.
 *
 * Paged and searched on the server, because the scope is now every company
 * on TeamLio rather than one partner. `q` matches names loosely and email /
 * user id / employee id exactly.
 */
export const getExternalEligibleUsers = (q?: string, limit = 50, cursor?: string | null) =>
  api.get<ExternalUserPage>('/eligible-users/external', {
    params: { q: q || undefined, limit, cursor: cursor || undefined },
  }).then(r => r.data);

// ─── Admin actions ────────────────────────────────────────────────────────────

export const updateMemberRole = (
  conversationId: string,
  userId: string,
  role: 'admin' | 'member',
) =>
  api.patch<{ user_id: string; role: string }>(
    `/conversations/${conversationId}/members/${userId}/role`,
    { role },
  ).then(r => r.data);

export const bulkDeleteMessages = (messageIds: string[]) =>
  api.post<{ deleted: string[]; count: number }>(
    '/messages/bulk-delete',
    { message_ids: messageIds },
  ).then(r => r.data);

// ─── Polls ────────────────────────────────────────────────────────────────────

export interface PollOption {
  id: string;
  text: string;
  vote_count: number;
  voted: boolean;
  voter_ids: string[];
}

export interface Poll {
  id: string;
  message_id: string;
  question: string;
  options: PollOption[];
  is_anonymous: boolean;
  allow_multiple: boolean;
  expires_at: string | null;
  is_closed: boolean;
  total_votes: number;
  created_at: string;
}

export const createPoll = (body: {
  conversation_id: string;
  question: string;
  options: Array<{ text: string }>;
  is_anonymous?: boolean;
  allow_multiple?: boolean;
  expires_at?: string | null;
}) => api.post<Poll>('/polls', body).then(r => r.data);

export const getPoll = (pollId: string) =>
  api.get<Poll>(`/polls/${pollId}`).then(r => r.data);

export const votePoll = (pollId: string, optionIds: string[]) =>
  api.post<Poll>(`/polls/${pollId}/vote`, { option_ids: optionIds }).then(r => r.data);

export const retractPollVote = (pollId: string) =>
  api.delete(`/polls/${pollId}/vote`).then(() => undefined);

export const closePoll = (pollId: string) =>
  api.post<Poll>(`/polls/${pollId}/close`).then(r => r.data);

export const listConversationPolls = (conversationId: string) =>
  api.get<Poll[]>(`/conversations/${conversationId}/polls`).then(r => r.data);

// ─── Translate ────────────────────────────────────────────────────────────────

export interface TranslateResponse {
  ok: boolean;
  reason?: string;
  message?: string;
  message_id?: string;
  target_lang?: string;
  translated?: string;
}

export const translateMessage = (messageId: string, targetLang: string): Promise<TranslateResponse> =>
  api.post<TranslateResponse>(
    `/messages/${messageId}/translate`,
    { target_lang: targetLang },
  ).then(r => r.data);

// ─── Summarize ────────────────────────────────────────────────────────────────

export interface SummarizeResponse {
  ok: boolean;
  reason?: string;
  message?: string;
  summary?: string;
  source?: string;
  message_count?: number;
}

export const summarizeConversation = (convId: string, style: 'brief' | 'detailed' = 'brief'): Promise<SummarizeResponse> =>
  api.post<SummarizeResponse>(
    `/conversations/${convId}/summarize`,
    { style },
  ).then(r => r.data);

export const summarizeThread = (messageId: string, style: 'brief' | 'detailed' = 'brief'): Promise<SummarizeResponse> =>
  api.post<SummarizeResponse>(
    `/threads/${messageId}/summarize`,
    { style },
  ).then(r => r.data);

// ─── Link Unfurl ──────────────────────────────────────────────────────────────

export interface UnfurlData {
  url: string;
  title?: string;
  description?: string;
  image_url?: string;
  favicon_url?: string;
  site_name?: string;
}

export const unfurlLink = (url: string) =>
  api.post<UnfurlData>(`/unfurl?url=${encodeURIComponent(url)}`).then(r => r.data);

// ─── Vanishing messages ───────────────────────────────────────────────────────

export const setVanishingTtl = (messageId: string, ttlSeconds: number | null) =>
  api.patch(`/messages/${messageId}/vanish`, { ttl_seconds: ttlSeconds }).then(r => r.data);

/** Alias used by MessageBubble per-message disappear override. */
export const setMessageVanish = setVanishingTtl;

// ─── Notification preferences ─────────────────────────────────────────────────

export const updateNotifyPref = (
  conversationId: string,
  body: { notify_mode: 'ALL' | 'MENTIONS' | 'NOTHING' | 'KEYWORDS'; notify_keywords?: string[]; muted_until?: string | null },
) => api.patch(`/conversations/${conversationId}/notify`, body).then(r => r.data);

// ─── Meetings ─────────────────────────────────────────────────────────────────

export interface Meeting {
  id: string;
  meet_url: string;
  title?: string;
  status: string;
  scheduled_at?: string;
  duration_min: number;
  participant_ids: string[];
  created_at: string;
  requires_oauth?: boolean;
}

export async function createInstantMeet(input: {
  conversation_id: string;
  title?: string;
  notify_user_ids?: string[];
}): Promise<Meeting> {
  const r = await api.post<Meeting>('/meet/instant', input);
  return r.data;
}

// ─── Google OAuth (Meet / Calendar / Drive) ──────────────────────────────────

export interface GoogleCapabilities {
  meet: boolean;
  calendar: boolean;
  drive: boolean;
}

export interface GoogleOauthStatus {
  connected: boolean;
  expires_at?: string;
  scopes?: string;
  // Per-feature grants. `connected` alone is NOT enough to know Drive works:
  // anyone who connected before Drive was added has a token without that scope
  // and must re-consent. Always gate Drive UI on capabilities.drive.
  capabilities?: GoogleCapabilities;
}

export const getGoogleOauthAuthUrl = () =>
  api.get<{ auth_url: string }>('/oauth/google/authorize').then(r => r.data);

export const getGoogleOauthStatus = () =>
  api.get<GoogleOauthStatus>('/oauth/google/status').then(r => r.data);

export const disconnectGoogleOauth = () =>
  api.post('/oauth/google/disconnect').then(() => undefined);

// Credentials the Google Picker needs in the browser. Fetched per open rather
// than cached: the access token is short-lived, and the server refreshes it.
// 428 here means the Drive grant is missing — open the consent popup.
export const getGooglePickerSession = () =>
  api
    .get<{ access_token: string; developer_key: string; app_id: string }>(
      '/oauth/google/picker-session'
    )
    .then(r => r.data);

export async function scheduleMeet(input: {
  conversation_id: string;
  title: string;
  description?: string;
  scheduled_at: string;       // ISO
  duration_min: number;
  participant_ids?: string[];
}): Promise<Meeting> {
  const r = await api.post<Meeting>('/meet/schedule', input);
  return r.data;
}

export const getMeeting = (meetingId: string) =>
  api.get<Meeting>(`/meet/${meetingId}`).then(r => r.data);

// ─── Web Push ─────────────────────────────────────────────────────────────────

export const subscribePush = (sub: { endpoint: string; p256dh: string; auth: string; user_agent?: string }) =>
  api.post('/push/subscribe', sub).then(r => r.data);

export const unsubscribePush = (endpoint: string) =>
  api.delete(`/push/unsubscribe?endpoint=${encodeURIComponent(endpoint)}`).then(() => undefined);

// ─── Voice messages ───────────────────────────────────────────────────────────

export const uploadVoiceMessage = (conversationId: string, blob: Blob, durationSeconds: number, threadParentId?: string) => {
  const form = new FormData();
  form.append('file', blob, 'voice-message.webm');
  form.append('message_type', 'voice');
  form.append('duration_seconds', String(Math.round(durationSeconds)));
  if (threadParentId) form.append('thread_parent_id', threadParentId);
  return api.post<Message>(`/conversations/${conversationId}/files`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
    transformRequest: [(data) => data],
  }).then(r => r.data);
};

// ─── Forward ──────────────────────────────────────────────────────────────────

export const forwardMessage = (messageId: string, conversationIds: string[], comment?: string) =>
  api.post<{ forwarded_count: number; message_ids: string[] }>(
    `/messages/${messageId}/forward`,
    { conversation_ids: conversationIds, comment },
  ).then(r => r.data);

// ─── Reminders ────────────────────────────────────────────────────────────────

export interface Reminder {
  id: string;
  user_id: string;
  message_id: string | null;
  conversation_id: string | null;
  note: string | null;
  remind_at: string;
  delivered: boolean;
  created_at: string;
}

export const createReminder = (body: {
  message_id?: string;
  conversation_id?: string;
  note?: string;
  remind_at: string;
}) => api.post<Reminder>('/reminders', body).then(r => r.data);

export const listReminders = (conversationId?: string) =>
  api.get<Reminder[]>('/reminders', {
    params: conversationId ? { conversation_id: conversationId } : undefined,
  }).then(r => r.data);

export const deleteReminder = (id: string) =>
  api.delete(`/reminders/${id}`).then(() => undefined);

export const updateReminder = (id: string, body: { remind_at?: string; note?: string }) =>
  api.patch<Reminder>(`/reminders/${id}`, body).then(r => r.data);

// ─── Voice transcription ─────────────────────────────────────────────────────

export const transcribeVoiceMessage = (messageId: string) =>
  api.post<{ message_id: string; transcription: string; language: string }>(
    `/messages/${messageId}/transcribe`
  ).then(r => r.data);

// ─── WebSocket URL helper ─────────────────────────────────────────────────────

export function getChatWsUrl(token: string): string {
  const explicit = process.env.REACT_APP_CHAT_WS_URL;
  if (explicit) {
    return `${explicit}?token=${encodeURIComponent(token)}`;
  }
  const apiUrl = process.env.REACT_APP_CHAT_API_URL || 'http://localhost:8001/chat-api';
  const wsBase = apiUrl.replace(/^http/, 'ws');
  return `${wsBase}/ws/chat?token=${encodeURIComponent(token)}`;
}

// ─── Identity directory (replaces departmentApi) ─────────────────────

export interface Department {
  id: string;
  name: string | null;
  branch_id: string | null;
}

export interface Branch {
  id: string;
  name: string | null;
  headOfficeID: string | null;
}

export interface IdentityUser extends Omit<EligibleUser, 'designation' | 'department_id' | 'department_name'> {
  designation?: string | null;
  department_id?: string | null;
  department_name?: string | null;
  role?: string | null;
}

export async function searchUsers(q: string, branchId?: string): Promise<IdentityUser[]> {
  const params: Record<string, string> = { q };
  if (branchId) params.branch_id = branchId;
  const { data } = await api.get('/identity/users/search', { params });
  return data?.users ?? [];
}

export async function getUser(id: string): Promise<IdentityUser | null> {
  try {
    const { data } = await api.get(`/identity/users/${id}`);
    return data;
  } catch {
    return null;
  }
}

export async function listDepartments(): Promise<Department[]> {
  try {
    const { data } = await api.get('/identity/departments');
    return Array.isArray(data) ? data : [];
  } catch {
    return [];
  }
}

export interface DesignationOption {
  id: string;
  name: string;
}

export async function listDesignations(): Promise<DesignationOption[]> {
  try {
    const { data } = await api.get('/identity/designations');
    return Array.isArray(data) ? data : [];
  } catch {
    return [];
  }
}

export async function getDepartment(id: string): Promise<Department | null> {
  try {
    const { data } = await api.get(`/identity/departments/${id}`);
    return data;
  } catch {
    return null;
  }
}

export async function getBranch(id: string): Promise<Branch | null> {
  try {
    const { data } = await api.get(`/identity/branches/${id}`);
    return data;
  } catch {
    return null;
  }
}

// ─── FCM device registration ─────────────────────────────────────────

export interface FcmRegisterIn {
  fcm_token: string;
  device_id?: string;
  platform?: 'ios' | 'android' | 'web';
}

export async function registerFcmDevice(payload: FcmRegisterIn): Promise<void> {
  await api.post('/devices/register', payload);
}

export async function unregisterFcmDevice(deviceId: string): Promise<void> {
  await api.delete(`/devices/${deviceId}`);
}

// ─── Phase 12: Cross-org admin lifecycle ──────────────────────────────────────

/** Leave a cross-org conversation. Throws on 409 if caller is sole admin. */
export async function leaveConversation(convId: string): Promise<void> {
  await api.post(`/conversations/${convId}/leave`);
}

/** Transfer admin role to a same-org teammate. */
export async function transferAdmin(convId: string, toUserId: string): Promise<void> {
  await api.post(`/conversations/${convId}/admin/transfer`, { to_user_id: toUserId });
}
